/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-16 11:51:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useState } from 'react';
import { Modal, Icon } from '@shopify/polaris';
import { ViewMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';

import ProductDetail from 'src/components/views/common/product-list/product-detail';

import { formatMoney } from 'src/helpers/money-format';
import noProductImage from 'src/assets/images/product/no-product-image.png';

type Props = {
  products: Array<{}>,
  moneyFormat: string
};
type ComposedProps = Props & WithI18nProps;

const PRODUCT_PER_ROW = 6;

// Render component
const ProductsGrid = (props: ComposedProps) => {
  const { i18n, products, moneyFormat } = props;
  const [openDetail, setOpenDetail] = useState(false);
  const [detail, setDetail] = useState(undefined);

  // Open detail product
  const handleOpenDetail = product => {
    setDetail(product);
    setOpenDetail(true);
  };
  // Render product list
  const renderProductList = () => {
    if (!products.length) return '';

    // Render 6 products per row, 3 lines of products
    const productsPerRow = [];
    for (let i = 0; i < products.length; i++) {
      const rowIndex = Math.floor(i / PRODUCT_PER_ROW);
      if (!productsPerRow[rowIndex]) productsPerRow[rowIndex] = [];
      productsPerRow[rowIndex].push(products[i]);
    }

    // cause we use space-between, so fill out the row with empty block
    const renderProducts = () => {
      return (
        <>
          {productsPerRow.map((row, index) => {
            const leftToFillRow = 6 - (row.length % 6);
            return (
              <div className="productRow" key={`row-product-${index}`}>
                {row.map((product, productIndex) => {
                  const {
                    id,
                    title = '',
                    price_min = 0,
                    compare_at_price_min = 0,
                    images = {}
                  } = product;
                  const imageUrl = _.isEmpty(images)
                    ? noProductImage
                    : images[Object.keys(images)[0]];
                  return (
                    <div className="productItem" key={id + '-' + productIndex}>
                      <span style={{ display: 'none' }}>{id}</span>
                      <div
                        className={`product-image ${
                          _.isEmpty(images) ? 'product-image--no-image' : ''
                        }`}>
                        <div className="mask-view" onClick={() => handleOpenDetail(product)}>
                          <span>
                            <Icon source={ViewMinor} />
                            <span className="view-product">
                              {i18n.translate('AdminHTML.MerchandisingV2.Rule.viewProduct')}
                            </span>
                          </span>
                        </div>
                        {_.isEmpty(images) ? (
                          <img className="noImage" src={imageUrl} alt={title} />
                        ) : (
                          <img className="image" src={imageUrl} alt={title} />
                        )}
                      </div>
                      <p className="title">{title}</p>
                      <p>
                        <span className="priceMin">{formatMoney(price_min, moneyFormat)}</span>
                        {compare_at_price_min > 0 && (
                          <span className="comparedAtPrice">
                            {formatMoney(compare_at_price_min, moneyFormat)}
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })}
                {leftToFillRow < 6 &&
                  _.range(leftToFillRow).map(index => (
                    <div className="productItem" key={index + '-blank'}></div>
                  ))}
              </div>
            );
          })}
        </>
      );
    };

    return <div className="productList">{renderProducts()}</div>;
  };

  return (
    <div className="product-grid">
      {/* Product list */}
      {products.length ? renderProductList() : ''}
      <Modal
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        title={i18n.translate('AdminHTML.Product.Product.detail')}>
        <Modal.Section>
          <ProductDetail product={detail} moneyFormat={moneyFormat} />
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default withI18n()(ProductsGrid);
